/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-extra-boolean-cast */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DragAndDropResult, FileInfo, Layout } from "@/common/define";
import { GlobalState } from "@/common/global";
import {
  PayloadChecked,
  PayloadSelectedFileAndLayout,
  RootEpic,
} from "@/common/type-state";
import { merge } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { Lodash } from "@/utils/utils";
import { setAppLoading } from "./app.slice";
import { MultiViewerHelper } from "./helper";
import { deleteFLoatingVisibleByViewId } from "./panelVisible.slice";

const initState: MultiViewerHelper.MultiViewerState = {
  listViewerDisplay: [],
  viewActive: {
    viewId: "",
    baseFileId: null,
    extension: "",
    modelFileId: "",
  },
  layout: Layout.Full,
  listViewerVisible: [],
  listViewerBlank: [],
  listViewOnly: [],
  isMarkupDragging: false,
  synchronized: false,
};

const multiViewerSlice = createSlice({
  name: "multiViewer",
  initialState: initState,
  reducers: {
    setLayout(state, action: PayloadAction<Layout>) {
      state.layout = action.payload;
    },
    firstFileDisplay() {
      return;
    },
    setViewActive() {
      return;
    },
    addViewerDisplay() {
      return;
    },
    selectViewerMerge() {
      return;
    },
    addViewerDisplayComplete(
      state,
      action: PayloadAction<MultiViewerHelper.ResultHandleMultiView>
    ) {
      const { newListViewerDisplay, newListViewerVisible, newViewActive } =
        action.payload;
      newListViewerDisplay && (state.listViewerDisplay = newListViewerDisplay);
      newListViewerVisible && (state.listViewerVisible = newListViewerVisible);
      newViewActive && (state.viewActive = newViewActive);
      GlobalState.redrawViewer();
    },
    closeViewer() {
      return;
    },
    closeViewerComplete(
      state,
      action: PayloadAction<MultiViewerHelper.ResultHandleMultiView>
    ) {
      const {
        newListViewerBlank,
        newListViewerDisplay,
        newListViewerVisible,
        newViewActive,
        newLayout,
      } = action.payload;
      newListViewerBlank && (state.listViewerBlank = newListViewerBlank);
      newListViewerDisplay && (state.listViewerDisplay = newListViewerDisplay);
      newListViewerVisible && (state.listViewerVisible = newListViewerVisible);
      newViewActive && (state.viewActive = newViewActive);
      newLayout && (state.layout = newLayout);
    },
    checkedFile() {
      return;
    },
    selectedFile(state, action: PayloadAction<ViewId>) {
      const findFile = state.listViewerVisible.find(
        (f) => GlobalState.getViewId(f) === action.payload
      );
      const viewIdActive = state.viewActive
        ? GlobalState.getViewId(state.viewActive.viewId)
        : null;
      if (findFile && findFile !== viewIdActive) {
        state.viewActive = MultiViewerHelper.createViewActive(findFile, [
          ...state.listViewerDisplay,
        ]);
      }
    },
    setSynchronized(state, action: PayloadAction<boolean>) {
      state.synchronized = action.payload;
      GlobalState.setSynchronized(action.payload);
    },
    selectFileAndLayout() {
      return;
    },
    createViewOnly() {
      return;
    },
    dragAndDropViewer() {
      return;
    },
    updateStateHandleMultiView(
      state,
      action: PayloadAction<MultiViewerHelper.ResultHandleMultiView>
    ) {
      const {
        newListViewerDisplay,
        newListViewerVisible,
        newViewActive,
        newListViewerBlank,
        newLayout,
        newListViewOnly,
      } = action.payload;
      newListViewerDisplay && (state.listViewerDisplay = newListViewerDisplay);
      newListViewerVisible && (state.listViewerVisible = newListViewerVisible);
      newListViewerBlank && (state.listViewerBlank = newListViewerBlank);
      newViewActive && (state.viewActive = newViewActive);
      newLayout && (state.layout = newLayout);
      newListViewOnly && (state.listViewOnly = newListViewOnly);
    },
    errorMultiViewer() {
      return;
    },
    setIsMarkupDragging(state, action: PayloadAction<boolean>) {
      state.isMarkupDragging = action.payload;
      const viewIdActive = state.viewActive
        ? GlobalState.getViewId(state.viewActive.viewId)
        : "";
      GlobalState.mapDraggingActive.set(viewIdActive, action.payload);
    },
    updateViewActive() {
      return;
    },
    activateMarkupViewSelected() {
      return;
    },
    onChangeViewer() {
      return;
    },
    deleteStateByViewIdMultiView(state, action: PayloadAction<ViewId>) {
      const viewId = action.payload;
      const listViewDisplay = Lodash.cloneDeep(state.listViewerDisplay);
      state.listViewerDisplay = listViewDisplay.filter(
        (v) => v.viewId !== viewId
      );
    },
  },
});

/* -------------------- Epics -------------------- */
const firstFileDisplay$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(firstFileDisplay.match),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const [firstFile] = state.filesList.filesList;
      if (firstFile?.viewId) {
        return [addViewerDisplay([firstFile])];
      }
      return [];
    })
  );

const closeViewer$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(closeViewer.match),
    withLatestFrom(state$),
    concatMap(([{ payload: viewId }, state]) => {
      return merge(
        [setAppLoading(true)],
        MultiViewerHelper.closeViewerObser(viewId, state.multiViewer)
          .pipe(
            map((resultClose) =>
              multiViewerSlice.actions.closeViewerComplete(resultClose)
            )
          )
          .pipe(catchError(() => [setAppLoading(false)]))
      );
    })
  );

const addViewerDisplay$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(addViewerDisplay.match),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      return MultiViewerHelper.addViewerDisplayObser(
        action.payload,
        state.multiViewer
      ).pipe(
        map((resultAddView) =>
          multiViewerSlice.actions.addViewerDisplayComplete(resultAddView)
        )
      );
    })
  );

export const MultiViewerEpics = [
  closeViewer$,
  addViewerDisplay$,
  firstFileDisplay$,
];

export const {
  setLayout,
  firstFileDisplay,
  setViewActive,
  errorMultiViewer,
  addViewerDisplay,
  selectViewerMerge,
  closeViewer,
  closeViewerComplete,
  checkedFile,
  selectedFile,
  selectFileAndLayout,
  createViewOnly,
  dragAndDropViewer,
  setIsMarkupDragging,
  updateViewActive,
  activateMarkupViewSelected,
  onChangeViewer,
  setSynchronized,
  deleteStateByViewIdMultiView,
} = multiViewerSlice.actions;

export default multiViewerSlice.reducer;
